import {
  DataRoomReportAction,
  FILE_UPLOAD_ERROR_CODE,
  REPORT_ACTION_TYPE,
} from './types';
import LockclosedIcon from 'icons/Lockclosed.icon';
import LogoutIcon from 'icons/Logout.icon';
import DocumentIcon from 'icons/Document.icon';
import UploadCloudIcon from 'icons/UploadCloud.icon';
import XIcon from 'icons/X.icon';
import TrashIcon from 'icons/Trash.icon';
import CheckIcon from 'icons/Check.icon';
import ClockIcon from 'icons/Clock.icon';
import ChatIcon from 'icons/Chat.icon';
import { IconProp } from 'models/Icon';
import {
  DATA_ROOM_LOGIN_SOURCE,
  DataRoomLoginSource,
} from 'models/DataRoomLoginSource';
import SendIcon from 'icons/Send.icon';
import LinkIcon from 'icons/Link.icon';
import HomeIcon from 'icons/Home.icon';

export const REPORT_ACTION_TRANSLATION = {
  [REPORT_ACTION_TYPE.LOG_IN]: 'reportLogIn',
  [REPORT_ACTION_TYPE.LOG_OUT]: 'reportLogOut',
  [REPORT_ACTION_TYPE.DOCUMENT_VIEWED]: 'reportDocumentViewed',
  [REPORT_ACTION_TYPE.DOCUMENT_DOWNLOADED]: 'reportDocumentDownloaded',
  [REPORT_ACTION_TYPE.DOCUMENT_PUBLISHED]: 'reportDocumentPublished',
  [REPORT_ACTION_TYPE.DOCUMENT_RENAMED]: 'reportDocumentRenamed',
  [REPORT_ACTION_TYPE.DOCUMENT_DELETED]: 'reportDocumentDeleted',
  [REPORT_ACTION_TYPE.ACCESS_GRANTED]: 'reportAccessGranted',
  [REPORT_ACTION_TYPE.ACCESS_REVOKED]: 'reportAccessRevoked',
  [REPORT_ACTION_TYPE.TIME_SPENT]: 'reportTimeSpent',
  [REPORT_ACTION_TYPE.DRAFTED_QUESTION]: 'drafted_question-c',
  [REPORT_ACTION_TYPE.BUY_SIDE_RETURNED_QUESTION]:
    'buy_side_returned_question-c',
  [REPORT_ACTION_TYPE.SUBMITTED_QUESTION]: 'submitted_question-c',

  [REPORT_ACTION_TYPE.SELL_SIDE_ACCEPTED_QUESTION]:
    'sell_side_accepted_question-c',
  [REPORT_ACTION_TYPE.SELL_SIDE_REJECTED_QUESTION]:
    'sell_side_rejected_question-c',
  [REPORT_ACTION_TYPE.DRAFTED_ANSWER]: 'drafted_answer-c',
  [REPORT_ACTION_TYPE.SELL_SIDE_APPROVED_ANSWER]: 'sell_side_approved_answer-c',
  [REPORT_ACTION_TYPE.SELL_SIDE_REJECTED_ANSWER]: 'sell_side_rejected_answer-c',
  [REPORT_ACTION_TYPE.FAQ_PUBLISHED]: 'faq_published-c',

  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_FILE]:
    'changedSecurityForFile',
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_DATA_ROOM]:
    'changedSecurityForDR',
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_SHAREABLE_LINK]:
    'changedSecurityForSL',
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_RFI]:
    'changedSecurityForRFI',

  [REPORT_ACTION_TYPE.SECURE_LINK_TERMS_AND_CONDITIONS_ACCEPTED]:
    'tcAcceptedForSecureLink',
  [REPORT_ACTION_TYPE.SECURE_LINK_TERMS_AND_CONDITIONS_REJECTED]:
    'tcRejectedForSecureLink',
  [REPORT_ACTION_TYPE.SECURE_LINK_NDA_SIGNED]: 'ndaSignedForSecureLink',

  [REPORT_ACTION_TYPE.DOCUMENT_UPLOAD_FAILED]: 'reportCouldntUploadFile',

  [REPORT_ACTION_TYPE.DOCUMENT_UPLOAD_SECURITY_LAYERS_FAILED]:
    'fileUploadSecurityLayersFailed',
} as Record<DataRoomReportAction, string>;

export const REPORT_ACTION_ICONS = {
  [REPORT_ACTION_TYPE.LOG_IN]: LockclosedIcon,
  [REPORT_ACTION_TYPE.LOG_OUT]: LogoutIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_VIEWED]: DocumentIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_DOWNLOADED]: DocumentIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_PUBLISHED]: UploadCloudIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_RENAMED]: DocumentIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_DELETED]: TrashIcon,
  [REPORT_ACTION_TYPE.ACCESS_GRANTED]: CheckIcon,
  [REPORT_ACTION_TYPE.ACCESS_REVOKED]: XIcon,
  [REPORT_ACTION_TYPE.TIME_SPENT]: ClockIcon,
  [REPORT_ACTION_TYPE.DRAFTED_QUESTION]: ChatIcon,
  [REPORT_ACTION_TYPE.BUY_SIDE_RETURNED_QUESTION]: ChatIcon,
  [REPORT_ACTION_TYPE.SUBMITTED_QUESTION]: ChatIcon,
  [REPORT_ACTION_TYPE.SELL_SIDE_ACCEPTED_QUESTION]: ChatIcon,
  [REPORT_ACTION_TYPE.SELL_SIDE_REJECTED_QUESTION]: ChatIcon,
  [REPORT_ACTION_TYPE.DRAFTED_ANSWER]: ChatIcon,
  [REPORT_ACTION_TYPE.SELL_SIDE_APPROVED_ANSWER]: ChatIcon,
  [REPORT_ACTION_TYPE.SELL_SIDE_REJECTED_ANSWER]: ChatIcon,
  [REPORT_ACTION_TYPE.FAQ_PUBLISHED]: ChatIcon,
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_FILE]: DocumentIcon,
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_DATA_ROOM]: HomeIcon,
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_SHAREABLE_LINK]: LinkIcon,
  [REPORT_ACTION_TYPE.CHANGED_SECURITY_SETTINGS_FOR_RFI]: SendIcon,
  [REPORT_ACTION_TYPE.SECURE_LINK_TERMS_AND_CONDITIONS_ACCEPTED]: CheckIcon,
  [REPORT_ACTION_TYPE.SECURE_LINK_TERMS_AND_CONDITIONS_REJECTED]: XIcon,
  [REPORT_ACTION_TYPE.SECURE_LINK_NDA_SIGNED]: CheckIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_UPLOAD_FAILED]: XIcon,
  [REPORT_ACTION_TYPE.DOCUMENT_UPLOAD_SECURITY_LAYERS_FAILED]: XIcon,
} as Record<DataRoomReportAction, IconProp>;

// A log in row keeps the single `log_in` action and varies only its copy, so the
// activity log filter stays one "Log in" option.
export const LOG_IN_SOURCE_TRANSLATION: Record<DataRoomLoginSource, string> = {
  [DATA_ROOM_LOGIN_SOURCE.PORTFOLIO]: 'reportLogInFromPortfolio',
  [DATA_ROOM_LOGIN_SOURCE.SECURE_LINK]: 'reportLogInFromSecureLink',
  [DATA_ROOM_LOGIN_SOURCE.NOTIFICATION]: 'reportLogInFromNotification',
  [DATA_ROOM_LOGIN_SOURCE.EMAIL]: 'reportLogInFromEmail',
  [DATA_ROOM_LOGIN_SOURCE.DIRECT]: 'reportLogInDirect',
};

export const FILE_UPLOAD_FAILED_COPY = {
  [FILE_UPLOAD_ERROR_CODE.FILE_INVALID_TYPE]: 'reportCouldntUploadFileType',
  [FILE_UPLOAD_ERROR_CODE.FILE_TOO_LARGE]: 'reportCouldntUploadFileLimit',
  [FILE_UPLOAD_ERROR_CODE.FILE_CANNOT_ADD_SECURITY_LAYERS]:
    'reportCouldntUploadFileSecurity',
  [FILE_UPLOAD_ERROR_CODE.INVALID_FILE_NAME]: 'reportCouldntUploadFileDamaged',
  [FILE_UPLOAD_ERROR_CODE.MISSING_FILE_NAME]: 'reportCouldntUploadFileDamaged',
  [FILE_UPLOAD_ERROR_CODE.DR_UPLOAD_STORAGE_FULL]:
    'reportCouldntUploadFileStorageFull',
};
